import React, { useEffect } from 'react';
import { AlertTriangle, Trash2, X, Clock, MapPin } from 'lucide-react';
import { EventItem } from '../../core/types.ts';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  event: EventItem | null;
  onClose: () => void;
  onConfirm: () => void;
}

export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  event,
  onClose,
  onConfirm,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !event) return null;

  const formatTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return (
        d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
        ' at ' +
        d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
      );
    } catch (_e) {
      return iso;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content delete-confirm-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
      >
        <div className="modal-header delete-modal-header">
          <div className="delete-modal-title-wrapper">
            <div className="delete-icon-badge">
              <AlertTriangle size={22} strokeWidth={2} />
            </div>
            <div>
              <h3 id="delete-modal-title" className="modal-title">Delete Event</h3>
              <p className="delete-modal-subtitle">This action cannot be undone.</p>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close modal">
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="modal-body delete-modal-body">
          <p className="delete-warning-text">
            Are you sure you want to permanently delete this event from the O-Weeks schedule?
          </p>

          <div className="delete-event-summary-card">
            <div className="delete-event-title-row">
              <span className="delete-event-name">{event.title}</span>
              <span className="cat-badge" style={{ background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text-primary)' }}>
                {event.category}
              </span>
            </div>

            <div className="delete-event-details">
              <div className="meta-item">
                <Clock size={14} strokeWidth={1.8} />
                <span>{formatTime(event.start)}</span>
              </div>
              {event.location && (
                <div className="meta-item">
                  <MapPin size={14} strokeWidth={1.8} />
                  <span>{event.location}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} autoFocus>
            <Trash2 size={16} strokeWidth={2} />
            <span>Delete Event</span>
          </button>
        </div>
      </div>
    </div>
  );
};
